import { Button } from '@cluey/cluey-components';
import moment from 'moment';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ContainerLayout } from '../../../../common/hub/ContainerLayout';
import EnrolmentLayout from '../../../../common/hub/EnrolmentLayout';
import { ShadowContainer } from '../../../../components/ShadowContainer';
import useRedirectInvalidSession from '../../../../hooks/session/useRedirectInvalidSession';
import { CheckIcon } from '../../../../icons';
import { getSpecificActiveEnrolment } from '../../../../selectors/enrolmentSelector';
import { getTimezone } from '../../../../selectors/hubSelector';
import type { Enrolment, Session, SessionPolicy } from '../../../../types/hubInterfaces';
import { DATE_FORMAT_TYPE_1, SESSION_TIME_FORMAT } from '../../../../util/constants';
import { PATH_HUB_SESSIONS_PAGE, PATH_HUB_VOUCHERS } from '../../../../util/pagePath';
import { usePageHeader } from '../../../../v2/app-shell/usePageHeader';

interface HistoryState {
  sessionPolicy: SessionPolicy;
  sessionToSkip: Session;
}

const SuccessWithVoucher = () => {
  const history = useHistory<HistoryState>();
  const {
    location: { state },
  } = history;
  const sessionToSkip = state?.sessionToSkip;
  const timezone: string = useSelector(getTimezone);
  const enrolment: Enrolment = useSelector(
    getSpecificActiveEnrolment({ enrolmentId: sessionToSkip.uniqueEnrolmentId })
  );
  const date = moment(sessionToSkip?.startTime).tz(timezone).format(DATE_FORMAT_TYPE_1);
  const time = `${moment(sessionToSkip?.startTime).tz(timezone).format(SESSION_TIME_FORMAT)}
    - ${moment(sessionToSkip?.endTime).tz(timezone).format(SESSION_TIME_FORMAT)}`;
  const { setHeader } = usePageHeader();

  useEffect(() => {
    setHeader({
      label: `Cancel session (inform ${sessionToSkip?.tutor})`,
      showIcon: false,
      showBackButton: true,
      onBackClick: () => history.push(PATH_HUB_SESSIONS_PAGE),
    });
  }, [sessionToSkip?.tutor, setHeader, history]);

  useRedirectInvalidSession(sessionToSkip);

  return (
    <EnrolmentLayout
      sectionHeading={`${sessionToSkip.student} ${sessionToSkip.subject}`}
      mainHeading="Cancel confirmed"
      showEnrolmentStatus={false}
      showSummerModeEnrolmentStatus={false}
      enrolmentId={sessionToSkip.uniqueEnrolmentId}
    >
      <ContainerLayout>
        <ShadowContainer className="flex flex-col gap-y-10">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-[60px] w-[60px] items-center justify-center rounded-full">
              <CheckIcon className="text-white" width={60} height={60} />
            </div>
            <h1 className="text-center font-merriweather text-2xl font-bold md:text-[32px]">Session cancelled</h1>
          </div>
          <div className="mx-auto flex flex-col gap-y-4 md:w-4/5">
            <p className="text-center text-lg">
              {sessionToSkip.student}'s {enrolment.yearLevel} {sessionToSkip.subject}
              {sessionToSkip.studentSessionType.includes('holiday') ? ' school holiday' : ''} session has been
              cancelled:
            </p>
            <div className="font-semibold">
              <p className="text-center text-lg">{date}</p>
              <p className="text-center text-lg">{time}</p>
            </div>
            <p className="text-center text-lg">
              You will receive a voucher that can be used by booking make-up sessions. Vouchers are valid for 75 days
              from the date of issue.
            </p>
          </div>
          <Button
            onClick={() => {
              history.push(PATH_HUB_VOUCHERS);
            }}
            size="sm"
            appearance="reverse"
            className="mx-auto flex h-10 w-fit items-center justify-center !border text-sm !font-medium tracking-wide text-grey-400 md:text-base"
          >
            View vouchers
          </Button>
        </ShadowContainer>
      </ContainerLayout>
    </EnrolmentLayout>
  );
};

export default SuccessWithVoucher;
